import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

@Component({
  selector: 'app-metadata-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './metadata-card.component.html',
  styleUrl: './metadata-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MetadataCardComponent {
  @Input() data: Record<string, unknown> | { payload?: Record<string, unknown> } | null = null;
  @Input() fields: Array<
    | string
    | {
        key?: string;
        normaliceKey?: string;
        order?: number;
        label?: string;
      }
  > | null = null;

  trackByEntry(_index: number, entry: { key: string }): string {
    return entry.key;
  }

  getEntries(): Array<{ key: string; label: string; value: string }> {
    const source = this.getSource();
    const fieldList = this.getFieldList();

    if (!fieldList.length) {
      return Object.entries(source)
        .filter(([_, value]) => value !== null && value !== undefined && value !== '-')
        .map(([key, value]) => ({
          key,
          label: key,
          value: this.formatValue(value),
        }))
        .filter((entry) => entry.value.length > 0);
    }

    return fieldList
      .map((field) => {
        const rawValue = source[field.key];
        if (rawValue === null || rawValue === undefined || rawValue === '-') {
          return null;
        }
        return {
          key: field.key,
          label: field.label,
          value: this.formatValue(rawValue),
        };
      })
      .filter((entry): entry is { key: string; label: string; value: string } => {
        return Boolean(entry && entry.value.length > 0);
      });
  }

  private getSource(): Record<string, unknown> {
    const raw = this.data ?? {};
    if (raw && typeof raw === 'object') {
      const payload = (raw as { payload?: unknown }).payload;
      if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
        return payload as Record<string, unknown>;
      }
      return raw as Record<string, unknown>;
    }
    return {};
  }

  private getFieldList(): Array<{ key: string; order: number; label: string }> {
    if (!this.fields?.length) return [];

    return this.fields
      .map((field, index) => {
        if (typeof field === 'string') {
          return { key: field, order: index, label: field };
        }
        const key = field.key ?? field.normaliceKey ?? '';
        if (!key) return null;
        return {
          key,
          order: field.order ?? index,
          label: field.label ?? key,
        };
      })
      .filter((field): field is { key: string; order: number; label: string } => Boolean(field))
      .sort((a, b) => a.order - b.order);
  }

  private formatValue(value: unknown): string {
    if (Array.isArray(value)) {
      return value.map((item) => String(item)).join(', ');
    }
    return typeof value === 'string' ? value : String(value);
  }
}
